/**
 * @fileoverview Landing page cost donut chart with mock data.
 * Showcases the CostVsProfitDonut UI without requiring authentication.
 * @module features/landing/components/LandingCostDonut
 */

import { ReactElement, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Cell, Label, Pie, PieChart } from 'recharts'
import { Card } from '@/components/ui/card'
import { ChartContainer, ChartTooltip } from '@/components/ui/chart'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { getDonutChartColors } from '@/components/ui/chart-utils'
import { cn } from '@/lib/utils'
import { AnchoredHoverCard } from '@/shared/components/ui/AnchoredHoverCard'
import { RechartsHoverAdapter } from '@/shared/components/ui/RechartsHoverAdapter'
import { Thumbnail } from '@/shared/components/ui/Thumbnail'
import { useDonutTouchInteraction, renderStaticActiveShape } from '@/shared/hooks/useDonutTouchInteraction'
import { renderActiveDonutSector } from '@/shared/components/ui/AnimatedDonutSector'
import { DonutSliceGradients } from '@/shared/components/ui/DonutSliceGradients'
import { donutSliceGradientFill } from '@/shared/utils/donutGradient'
import { ExpandIcon } from '@/shared/components/ui/ExpandIcon'
import type { ChartConfig } from '@/components/ui/chart'

/**
 * Chart data point for cost breakdown visualization
 */
interface CostBreakdownData {
  name: string
  value: number
  fill: string
  percentage: number
  [key: string]: unknown
}

/**
 * Props for LandingCostDonut component
 */
interface LandingCostDonutProps {
  className?: string
}

/**
 * Mock data matching the LandingMetrics values
 */
const MOCK_DATA = {
  cogs: 66300,
  adSpend: 53700,
  businessCosts: 47100,
  profit: 74200,
  margin: 30.7,
}

/**
 * Mock product data for tooltips with image URLs
 */
const MOCK_PRODUCTS = {
  cogs: [
    { name: 'Monitor 27" 4K', value: 22400, imageUrl: '/landing-page/monitor-mock.webp' },
    { name: 'Mechanical Keyboard Pro', value: 12800, imageUrl: '/landing-page/keyboard-mock.webp' },
    { name: 'Wireless Headphones HD', value: 12400, imageUrl: '/landing-page/headphones-mock.webp' },
    { name: 'Studio Monitors 5"', value: 6200, imageUrl: '/landing-page/studiomonitors-mock.webp' },
  ],
  adSpend: [
    { name: 'Wireless Headphones HD', value: 22800, imageUrl: '/landing-page/headphones-mock.webp' },
    { name: 'Studio Monitors 5"', value: 13400, imageUrl: '/landing-page/studiomonitors-mock.webp' },
    { name: 'Gaming Controller Pro', value: 9200, imageUrl: '/landing-page/gamingcontroller-mock.webp' },
    { name: 'Power Bank 20000mAh', value: 3400, imageUrl: '/landing-page/powerbank-mock.webp' },
  ],
  profit: [
    { name: 'Monitor 27" 4K', value: 43500, imageUrl: '/landing-page/monitor-mock.webp' },
    { name: 'Mechanical Keyboard Pro', value: 41600, imageUrl: '/landing-page/keyboard-mock.webp' },
    { name: 'Gaming Mouse Elite', value: 37000, imageUrl: '/landing-page/mouse-mock.webp' },
    { name: 'USB Microphone Studio', value: 6700, imageUrl: '/landing-page/microphone-mock.webp' },
  ],
  businessCosts: [
    { name: 'Shipping', value: 15800 },
    { name: 'Warehouse & Storage', value: 10900 },
    { name: 'Payment fees', value: 7900 },
    { name: 'Software subscriptions', value: 6000 },
    { name: 'Accounting', value: 4000 },
    { name: 'Office tools', value: 2500 },
  ],
}

/**
 * Creates chart configuration for ShadCN charts
 */
const chartConfig: ChartConfig = {
  cogs: {
    label: 'COGS',
    color: 'var(--chart-legend-cogs)',
  },
  adSpend: {
    label: 'Ad Spend',
    color: 'var(--chart-legend-adspend)',
  },
  overhead: {
    label: 'Business Costs',
    color: 'var(--chart-legend-overhead, var(--chart-legend-cogs))',
  },
  profit: {
    label: 'Profit',
    color: 'var(--chart-legend-oc)',
  },
}

/**
 * Formats a number as compact currency (e.g., 14.1K zł).
 */
function formatMoney(value: number): string {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K zł`
  }
  return `${value} zł`
}

/**
 * Tooltip item for cost breakdown
 */
interface CostTooltipItem {
  key: string
  name: string
  icon?: ReactElement | undefined
  value: number
  percentage?: string | undefined
}

/**
 * Tooltip content component for cost breakdown
 */
function CostTooltipContent({
  sliceName,
  sliceValue,
  sliceColor,
  items,
  noItemsText,
}: {
  sliceName: string
  sliceValue: number
  sliceColor: string
  items: CostTooltipItem[]
  noItemsText: string
}): ReactElement {
  if (!items.length) {
    return (
      <div className="text-base font-medium text-center p-4 text-muted-foreground">
        {noItemsText}
      </div>
    )
  }

  return (
    <Table className="table-fixed">
      <TableHeader>
        <TableRow className="hover:bg-transparent border-t-0 bg-black/10">
          <TableHead className="pl-4 pr-2 h-8">
            <span className="text-xs font-medium text-muted-foreground">
              {sliceName}
            </span>
          </TableHead>
          <TableHead className="w-36 pl-2 pr-4 h-8 text-right">
            <div className="flex items-center justify-end gap-1">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: sliceColor }}
              />
              <span className="text-xs font-semibold text-foreground tabular-nums">
                {formatMoney(sliceValue)}
              </span>
            </div>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.key} className="hover:bg-transparent">
            <TableCell className="pl-4 pr-2 py-2 align-middle">
              <div className="flex items-center gap-2 min-w-0">
                {item.icon && <span className="shrink-0">{item.icon}</span>}
                <span className="truncate text-sm text-muted-foreground">{item.name}</span>
              </div>
            </TableCell>
            <TableCell className="w-36 pl-2 pr-4 py-2 text-right align-middle">
              <div className="flex flex-col items-end">
                <span className="text-sm font-medium tabular-nums text-foreground whitespace-nowrap">
                  {formatMoney(item.value)}
                </span>
                {item.percentage && (
                  <span className="text-xs text-muted-foreground/60">
                    {item.percentage}
                  </span>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

/**
 * Landing page cost donut chart with mock data.
 * Mirrors the CostVsProfitDonut component but uses static mock data.
 *
 * @component
 */
export function LandingCostDonut({ className }: LandingCostDonutProps): ReactElement {
  const { t } = useTranslation('features-landing')
  const colors = getDonutChartColors()

  // Touch interaction hook for mobile/tablet
  const {
    isMobile,
    isTablet,
    isBelowDesktop,
    mobileSlice,
    tabletSlice,
    tabletAnchor,
    chartContainerRef,
    handleSliceClick,
    clearMobileSlice,
  } = useDonutTouchInteraction<CostBreakdownData>()

  // Calculate total for percentages
  const total = MOCK_DATA.cogs + MOCK_DATA.adSpend + MOCK_DATA.businessCosts + MOCK_DATA.profit

  // Transform mock data for Recharts format
  const chartData: CostBreakdownData[] = useMemo(() => {
    return [
      {
        name: t('demo.costDonut.slices.cogs'),
        value: MOCK_DATA.cogs,
        fill: colors.cogs,
        percentage: (MOCK_DATA.cogs / total) * 100,
      },
      {
        name: t('demo.costDonut.slices.adSpend'),
        value: MOCK_DATA.adSpend,
        fill: colors.adSpend,
        percentage: (MOCK_DATA.adSpend / total) * 100,
      },
      {
        name: t('demo.costDonut.slices.businessCosts'),
        value: MOCK_DATA.businessCosts,
        fill: colors.overhead,
        percentage: (MOCK_DATA.businessCosts / total) * 100,
      },
      {
        name: t('demo.costDonut.slices.profit'),
        value: MOCK_DATA.profit,
        fill: 'var(--chart-legend-oc)',
        percentage: (MOCK_DATA.profit / total) * 100,
      },
    ]
  }, [colors, total, t])

  // Color map for tooltip - use translated names as keys
  const colorMap = useMemo<Record<string, string>>(() => ({
    [t('demo.costDonut.slices.cogs')]: 'var(--chart-legend-cogs)',
    [t('demo.costDonut.slices.adSpend')]: 'var(--chart-legend-adspend)',
    [t('demo.costDonut.slices.businessCosts')]: 'var(--chart-legend-overhead)',
    [t('demo.costDonut.slices.profit')]: 'var(--chart-legend-oc)',
  }), [t])

  /**
   * Builds tooltip items with thumbnails and percentages
   */
  const buildTooltipItems = useCallback(
    (sliceName: string, sliceValue: number): CostTooltipItem[] => {
      const formatPercentage = (value: number): string => `${value.toFixed(1)}%`

      // Business costs don't have images
      if (sliceName === t('demo.costDonut.slices.businessCosts')) {
        return MOCK_PRODUCTS.businessCosts.map((item, idx) => ({
          key: `business-${idx}`,
          name: item.name,
          value: item.value,
          percentage: sliceValue > 0 ? formatPercentage((item.value / sliceValue) * 100) : undefined,
        }))
      }

      // Get products for the slice type
      const products = sliceName === t('demo.costDonut.slices.cogs')
        ? MOCK_PRODUCTS.cogs
        : sliceName === t('demo.costDonut.slices.adSpend')
          ? MOCK_PRODUCTS.adSpend
          : MOCK_PRODUCTS.profit

      return products.map((item, idx) => ({
        key: `${sliceName}-${idx}`,
        name: item.name,
        icon: <Thumbnail src={item.imageUrl} alt={item.name} size={40} />,
        value: item.value,
        percentage: sliceValue > 0 ? formatPercentage((item.value / sliceValue) * 100) : undefined,
      }))
    },
    [t]
  )

  /**
   * Builds tooltip data for mobile sheet and tablet popover
   */
  const buildTooltipData = useCallback(
    (slice: CostBreakdownData) => {
      const sliceName = slice.name
      const sliceValue = slice.value
      const sliceColor = colorMap[sliceName] ?? 'var(--chart-legend-cogs)'
      const items = buildTooltipItems(sliceName, sliceValue)

      return { sliceColor, items }
    },
    [colorMap, buildTooltipItems]
  )

  return (
    <Card className={cn('rounded-xl border border-border/40 bg-card/30 overflow-hidden', className)}>
      {/* Header section — static clone of the dashboard's ChartCardHeader strip */}
      <div className="flex items-center justify-between px-6 pt-5 pb-4">
        <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {t('demo.costDonut.title')}
        </h3>
        <ExpandIcon />
      </div>
      {/* Chart section */}
      <div ref={chartContainerRef} className="px-6 pt-4 pb-8">
        {/* Donut Chart with Center Labels */}
        <ChartContainer
          id="landing-cost-donut"
          config={chartConfig}
          className="mx-auto aspect-[1/1] w-full [&_.recharts-surface]:overflow-visible"
        >
          <PieChart margin={{ top: 20, left: 20, right: 20, bottom: 20 }}>
            <DonutSliceGradients idPrefix="landing-cost-donut-slice" colors={chartData.map((d) => d.fill)} />
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius="55%"
              outerRadius="70%"
              paddingAngle={4}
              cornerRadius={8}
              dataKey="value"
              stroke="none"
              isAnimationActive={false}
              activeShape={isBelowDesktop ? renderStaticActiveShape : renderActiveDonutSector}
              onClick={(_, index) => {
                const slice = chartData[index]
                if (slice) handleSliceClick(slice)
              }}
              labelLine={{
                stroke: 'var(--muted-foreground)',
                strokeWidth: 1,
                strokeDasharray: '3 3',
              }}
              label={({ cx, cy, midAngle = 0, outerRadius, payload }) => {
                const RADIAN = Math.PI / 180
                const valueText = formatMoney(payload.value)
                const radius = Number(outerRadius) + (isBelowDesktop ? 16 : 20)
                const x = cx + radius * Math.cos(-midAngle * RADIAN)
                const y = cy + radius * Math.sin(-midAngle * RADIAN)
                const isRightSide = Math.cos(-midAngle * RADIAN) > 0

                // Mobile/tablet: show short labels
                if (isBelowDesktop) {
                  type SliceKey = 'cogs' | 'adSpend' | 'businessCosts' | 'profit'
                  const sliceKeyMap: Record<string, SliceKey> = {
                    [t('demo.costDonut.slices.cogs')]: 'cogs',
                    [t('demo.costDonut.slices.adSpend')]: 'adSpend',
                    [t('demo.costDonut.slices.businessCosts')]: 'businessCosts',
                    [t('demo.costDonut.slices.profit')]: 'profit',
                  }
                  const shortLabelMap: Record<SliceKey, string> = {
                    cogs: t('demo.costDonut.shortLabels.cogs'),
                    adSpend: t('demo.costDonut.shortLabels.adSpend'),
                    businessCosts: t('demo.costDonut.shortLabels.businessCosts'),
                    profit: t('demo.costDonut.shortLabels.profit'),
                  }
                  const sliceKey = sliceKeyMap[payload.name] ?? 'cogs'
                  const shortLabel = shortLabelMap[sliceKey]

                  return (
                    <g>
                      <text
                        x={x}
                        y={y - 6}
                        textAnchor={isRightSide ? 'start' : 'end'}
                        dominantBaseline="middle"
                        className="font-bold fill-foreground"
                        style={{ fontSize: 14 }}
                      >
                        {valueText}
                      </text>
                      <text
                        x={x}
                        y={y + 12}
                        textAnchor={isRightSide ? 'start' : 'end'}
                        className="fill-muted-foreground"
                        style={{ fontSize: 12 }}
                      >
                        {shortLabel}
                      </text>
                    </g>
                  )
                }

                // Desktop: full labels with wrapping
                const labelText = `${payload.name} ${payload.percentage.toFixed(1)}%`
                const MAX_LENGTH = 18
                const shouldWrap = labelText.length > MAX_LENGTH

                let line1 = labelText
                let line2 = ''

                if (shouldWrap) {
                  const words = payload.name.split(' ')
                  const percentage = `${payload.percentage.toFixed(1)}%`

                  if (words.length > 1) {
                    line1 = payload.name
                    line2 = percentage
                  } else {
                    const mid = Math.floor(labelText.length / 2)
                    const spaceIndex = labelText.lastIndexOf(' ', mid)
                    if (spaceIndex > 0) {
                      line1 = labelText.substring(0, spaceIndex)
                      line2 = labelText.substring(spaceIndex + 1)
                    }
                  }
                }

                return (
                  <g>
                    <text
                      x={x}
                      y={shouldWrap ? y - 21 : y - 15}
                      textAnchor={isRightSide ? 'start' : 'end'}
                      dominantBaseline="middle"
                      className="font-bold fill-foreground"
                      style={{ fontSize: 16 }}
                    >
                      {valueText}
                    </text>
                    <g transform={`translate(${x}, ${shouldWrap ? y + 9 : y + 5})`}>
                      <circle
                        cx={isRightSide ? 5 : -5}
                        cy={shouldWrap ? -6 : 0}
                        r={3}
                        fill={payload.fill}
                      />
                      <text
                        x={isRightSide ? 12 : -12}
                        textAnchor={isRightSide ? 'start' : 'end'}
                        className="fill-muted-foreground"
                        style={{ fontSize: 13 }}
                      >
                        <tspan
                          x={isRightSide ? 12 : -12}
                          dy={shouldWrap ? '-0.6em' : '0'}
                          dominantBaseline="middle"
                        >
                          {line1}
                        </tspan>
                        {shouldWrap && (
                          <tspan
                            x={isRightSide ? 12 : -12}
                            dy="1.2em"
                            dominantBaseline="middle"
                          >
                            {line2}
                          </tspan>
                        )}
                      </text>
                    </g>
                  </g>
                )
              }}
            >
              {chartData.map((_, index) => (
                <Cell key={`cell-${index}`} fill={donutSliceGradientFill('landing-cost-donut-slice', index)} />
              ))}
              <Label
                content={({ viewBox }) => {
                  if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                    return (
                      <text
                        x={viewBox.cx}
                        y={viewBox.cy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) - 10}
                          className={cn('font-bold', isBelowDesktop ? 'text-xl' : 'text-3xl')}
                          fill="var(--color-positive)"
                        >
                          +{MOCK_DATA.margin.toFixed(1)}%
                        </tspan>
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) + 16}
                          className={cn(isBelowDesktop ? 'text-sm' : 'text-base')}
                          fill="var(--muted-foreground)"
                        >
                          {t('demo.costDonut.margin')}
                        </tspan>
                      </text>
                    )
                  }
                }}
              />
            </Pie>
            <ChartTooltip
              cursor={false}
              content={
                <RechartsHoverAdapter
                  dataChartId="chart-landing-cost-donut"
                  width={450}
                  estimatedHeight={420}
                  disabled={isBelowDesktop}
                  animatePosition={false}
                >
                  {({ payload }) => {
                    type SlicePayload = { value?: number; name?: string }
                    const p: SlicePayload | null =
                      Array.isArray(payload) && payload.length
                        ? (payload[0] as SlicePayload)
                        : null
                    if (!p) return null

                    const sliceName = String(p.name ?? '')
                    const sliceValue = Number(p.value ?? 0)
                    if (!sliceName || sliceValue === 0) return null

                    const sliceColor = colorMap[sliceName] ?? 'var(--chart-legend-cogs)'
                    const items = buildTooltipItems(sliceName, sliceValue)

                    return (
                      <CostTooltipContent
                        sliceName={sliceName}
                        sliceValue={sliceValue}
                        sliceColor={sliceColor}
                        items={items}
                        noItemsText={t('demo.costDonut.noItems', { sliceName })}
                      />
                    )
                  }}
                </RechartsHoverAdapter>
              }
              wrapperStyle={{ display: 'none' }}
            />
          </PieChart>
        </ChartContainer>
      </div>

      {/* Mobile Sheet for tooltip content */}
      {isMobile && (
        <Sheet open={!!mobileSlice} onOpenChange={(open) => !open && clearMobileSlice()}>
          <SheetContent
            side="bottom"
            showCloseButton={false}
            className={cn(
              'max-h-[85vh] overflow-y-auto rounded-t-2xl',
              'bg-[var(--glass-surface-dark)]'
            )}
          >
            <SheetHeader className="sr-only">
              <SheetTitle>{mobileSlice?.name ?? 'Details'}</SheetTitle>
              <SheetDescription>Breakdown by product</SheetDescription>
            </SheetHeader>
            <div className="pb-safe">
              {mobileSlice && (() => {
                const sliceName = mobileSlice.name
                const sliceValue = mobileSlice.value
                const { sliceColor, items } = buildTooltipData(mobileSlice)

                return (
                  <CostTooltipContent
                    sliceName={sliceName}
                    sliceValue={sliceValue}
                    sliceColor={sliceColor}
                    items={items}
                    noItemsText={t('demo.costDonut.noItems', { sliceName })}
                  />
                )
              })()}
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* Tablet popover - uses AnchoredHoverCard positioned at chart center */}
      {isTablet && tabletSlice && tabletAnchor && (() => {
        const sliceName = tabletSlice.name
        const sliceValue = tabletSlice.value
        const { sliceColor, items } = buildTooltipData(tabletSlice)

        return (
          <AnchoredHoverCard
            active={true}
            anchor={tabletAnchor}
            width={450}
            estimatedHeight={420}
            margin={8}
            offset={{ x: 0, y: 0 }}
            inert={false}
          >
            <CostTooltipContent
              sliceName={sliceName}
              sliceValue={sliceValue}
              sliceColor={sliceColor}
              items={items}
              noItemsText={t('demo.costDonut.noItems', { sliceName })}
            />
          </AnchoredHoverCard>
        )
      })()}
    </Card>
  )
}
